import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { createSong, getSongById, updateSong } from '../../services/songService';
import { musicalService } from '../../services/musicalService';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import SuccessNotification from '../../components/ui/SuccessNotification';
import Icon from '../../components/AppIcon';
import OverviewSection from './components/OverviewSection';
import LevelOneSection from './components/LevelOneSection';
import LevelTwoSection from './components/LevelTwoSection';
import LevelThreeSection from './components/LevelThreeSection';
import LevelFourSection from './components/LevelFourSection';
import LevelFiveSection from './components/LevelFiveSection';
import TalentSection from './components/TalentSection';


const SongAdditionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [musicals, setMusicals] = useState([]);
  
  // Extract song ID from URL if editing
  const searchParams = new URLSearchParams(location?.search);
  const songId = searchParams?.get('id');
  const isEditing = !!songId;
  
  // Add talent ID extraction for video uploads
  const talentId = searchParams?.get('talentId') || null;
  
  // Add talent selection state
  const [selectedTalentId, setSelectedTalentId] = useState(null);
  const [selectedTalentData, setSelectedTalentData] = useState(null);
  
  // Form state - Updated to remove unwanted fields
  const [formData, setFormData] = useState({
    overview: {
      musical: '',
      songTitle: '',
      character: '',
      songPlacementInShow: '',
      emotionalCore: '',
      objective: '',
      vocalRange: '',
      musicalStyle: '',
      musicalKey: '',
      typeOfSong: ''
    },
    // Simplified song details - removed unwanted fields
    songDetails: {
      actNumber: 1,
      songOrder: null,
      isFeatured: false,
      audioUrl: ''
    },
    levelOne: {
      lyrics: ''
    },
    levelTwo: {
      basicDiscussion: ''
    },
    levelThree: {
      opening: {
        referenceLyricLine: '',
        description: ''
      },
      middle: {
        referenceLyricLine: '',
        description: ''
      },
      ending: {
        referenceLyricLine: '',
        description: ''
      }
    },
    levelFour: {
      backstory: '',
      arc: '',
      physicality: '',
      facialExpressions: '',
      connection: '',
      exercises: {
        exercise1: '',
        exercise2: '',
        exercise3: ''
      }
    },
    levelFive: {
      phrases: [
        {
          id: 1,
          phrase: '',
          emotion: '',
          acting: '',
          vocal: ''
        }
      ]
    }
  });
  
  // Load musicals on component mount
  useEffect(() => {
    loadMusicals();
  }, []);

  const loadMusicals = async () => {
    try {
      const { data: musicalsData, error: musicalsError } = await musicalService?.getMusicals();
      if (musicalsError) {
        console.error('Error loading musicals:', musicalsError);
        setError('Failed to load musicals list. Please try again.');
        return;
      }
      setMusicals(musicalsData || []);
    } catch (err) {
      console.error('Error in loadMusicals:', err);
      setError('An unexpected error occurred while loading musicals.');
    }
  };

  // Load existing song data if editing
  useEffect(() => {
    if (isEditing && songId) {
      loadExistingSong(songId);
    }
  }, [isEditing, songId]);

  const loadExistingSong = async (id) => {
    setIsInitialLoading(true);
    setError(null);
    
    try {
      const { data: existingSong, error: fetchError } = await getSongById(id);
      
      if (fetchError) {
        console.error('Error loading existing song:', fetchError);
        setError('Failed to load song data. Please try again.');
        return;
      }
      
      if (existingSong) {
        // Transform song data from database format to form format
        const transformedData = transformSongToFormData(existingSong);
        setFormData(transformedData);
      }
    } catch (err) {
      console.error('Error in loadExistingSong:', err);
      setError('An unexpected error occurred while loading the song.');
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Transform song data from database to form format - updated for cleaned schema
  const transformSongToFormData = (songData) => {
    const musicalTitle = songData?.musical?.title || '';
    
    return {
      overview: {
        musical: musicalTitle,
        songTitle: songData?.title || '',
        character: songData?.overview?.character || '',
        songPlacementInShow: `Act ${songData?.act_number}`,
        emotionalCore: songData?.overview?.emotionalCore || '',
        objective: songData?.overview?.objective || '',
        vocalRange: songData?.overview?.vocalRange || '',
        musicalStyle: '', // No longer stored separately
        musicalKey: songData?.key_signature || '',
        typeOfSong: songData?.overview?.typeOfSong || ''
      },
      songDetails: {
        actNumber: songData?.act_number || 1,
        songOrder: songData?.song_order || null,
        isFeatured: songData?.is_featured || false,
        audioUrl: songData?.audio_url || ''
      },
      levelOne: {
        lyrics: songData?.level1 || ''
      },
      levelTwo: {
        basicDiscussion: songData?.level2 || ''
      },
      levelThree: {
        opening: {
          referenceLyricLine: songData?.level3?.[0]?.referenceLyricLine || '',
          description: songData?.level3?.[0]?.description || ''
        },
        middle: {
          referenceLyricLine: songData?.level3?.[1]?.referenceLyricLine || '',
          description: songData?.level3?.[1]?.description || ''
        },
        ending: {
          referenceLyricLine: songData?.level3?.[2]?.referenceLyricLine || '',
          description: songData?.level3?.[2]?.description || ''
        }
      },
      levelFour: {
        backstory: songData?.level4?.backstory || '',
        arc: songData?.level4?.arc || '',
        physicality: songData?.level4?.physicality || '',
        facialExpressions: songData?.level4?.facialExpressions || '',
        connection: songData?.level4?.connection || '',
        exercises: {
          exercise1: songData?.level4?.exercises?.exercise1 || '',
          exercise2: songData?.level4?.exercises?.exercise2 || '',
          exercise3: songData?.level4?.exercises?.exercise3 || ''
        }
      },
      levelFive: {
        phrases: songData?.phrases || [
          {
            id: 1,
            phrase: '',
            emotion: '',
            acting: '',
            vocal: ''
          }
        ]
      }
    };
  };

  const handleSectionChange = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        ...data
      }
    }));
  };

  const handleTalentChange = (talentId, talentData) => {
    setSelectedTalentId(talentId);
    setSelectedTalentData(talentData);
  };

  const validateRequiredFields = () => {
    const { overview } = formData;
    
    // Only require Musical and Song Title fields per requirements
    if (!overview?.musical?.trim()) {
      throw new Error('Musical is required');
    }
    
    if (!overview?.songTitle?.trim()) {
      throw new Error('Song Title is required');
    }
  };

  // Helper function to find musical ID from name
  const findMusicalId = (musicalTitle) => {
    const musical = musicals?.find(m => m?.title === musicalTitle);
    return musical?.id || null;
  };

  // Transform form data to songs table format - updated for cleaned schema
  const transformFormDataToSong = (formData) => {
    const musicalId = findMusicalId(formData?.overview?.musical);
    
    if (!musicalId) {
      throw new Error('Selected musical not found. Please select a valid musical.');
    }

    return {
      title: formData?.overview?.songTitle?.trim(),
      musical_id: musicalId,
      act_number: formData?.songDetails?.actNumber || 1,
      song_order: formData?.songDetails?.songOrder || null,
      key_signature: formData?.overview?.musicalKey || null,
      is_featured: formData?.songDetails?.isFeatured || false,
      audio_url: formData?.songDetails?.audioUrl || null,
      talent_id: selectedTalentId,
      // Store analysis data in JSONB fields to match songs table structure
      overview: JSON.stringify({
        character: formData?.overview?.character || '',
        songPlacementInShow: formData?.overview?.songPlacementInShow || '',
        emotionalCore: formData?.overview?.emotionalCore || '',
        objective: formData?.overview?.objective || '',
        vocalRange: formData?.overview?.vocalRange || '',
        typeOfSong: formData?.overview?.typeOfSong || ''
      }),
      level1: formData?.levelOne?.lyrics || '',
      level2: formData?.levelTwo?.basicDiscussion || '',
      level3: JSON.stringify([
        {
          section: 'opening',
          referenceLyricLine: formData?.levelThree?.opening?.referenceLyricLine || '',
          description: formData?.levelThree?.opening?.description || ''
        },
        {
          section: 'middle',
          referenceLyricLine: formData?.levelThree?.middle?.referenceLyricLine || '',
          description: formData?.levelThree?.middle?.description || ''
        },
        {
          section: 'ending',
          referenceLyricLine: formData?.levelThree?.ending?.referenceLyricLine || '',
          description: formData?.levelThree?.ending?.description || ''
        }
      ]),
      level4: JSON.stringify({
        backstory: formData?.levelFour?.backstory || '',
        arc: formData?.levelFour?.arc || '',
        physicality: formData?.levelFour?.physicality || '',
        facialExpressions: formData?.levelFour?.facialExpressions || '',
        connection: formData?.levelFour?.connection || '',
        exercises: {
          exercise1: formData?.levelFour?.exercises?.exercise1 || '',
          exercise2: formData?.levelFour?.exercises?.exercise2 || '',
          exercise3: formData?.levelFour?.exercises?.exercise3 || ''
        }
      }),
      level5: JSON.stringify({}), // Level 5 is handled in phrases
      phrases: JSON.stringify(formData?.levelFive?.phrases || [])
    };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate required fields
      validateRequiredFields();
      
      // Transform form data to song format
      const songData = transformFormDataToSong(formData);
      
      // Automatically assign the logged-in user as created_by or updated_by
      if (isEditing) {
        songData.updated_by = user?.id;
      } else {
        songData.created_by = user?.id;
      }
      
      let result;
      if (isEditing) {
        result = await updateSong(songId, songData);
      } else {
        result = await createSong(songData);
      }
      
      if (result?.error) {
        throw new Error(result?.error || 'Failed to save song');
      }
      
      // Show success message
      setSuccessMessage(isEditing ? 'Song updated successfully!' : 'Song created successfully!');
      setShowSuccess(true);
      
      // Navigate to song list after short delay
      setTimeout(() => {
        navigate('/song-database-list');
      }, 2000);
      
    } catch (err) {
      console.error('Submit error:', err);
      setError(err?.message || 'Failed to save song. Please check your input and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/song-database-list');
  };

  const handleClearForm = () => {
    const confirmation = window?.confirm('Are you sure you want to clear all form data? This action cannot be undone.');
    if (confirmation) {
      setFormData({
        overview: {
          musical: '',
          songTitle: '',
          character: '',
          songPlacementInShow: '',
          emotionalCore: '',
          objective: '',
          vocalRange: '',
          musicalStyle: '',
          musicalKey: '',
          typeOfSong: ''
        },
        // Simplified song details - removed unwanted fields
        songDetails: {
          actNumber: 1,
          songOrder: null,
          isFeatured: false,
          audioUrl: ''
        },
        levelOne: {
          lyrics: ''
        },
        levelTwo: {
          basicDiscussion: ''
        },
        levelThree: {
          opening: {
            referenceLyricLine: '',
            description: ''
          },
          middle: {
            referenceLyricLine: '',
            description: ''
          },
          ending: {
            referenceLyricLine: '',
            description: ''
          }
        },
        levelFour: {
          backstory: '',
          arc: '',
          physicality: '',
          facialExpressions: '',
          connection: '',
          exercises: {
            exercise1: '',
            exercise2: '',
            exercise3: ''
          }
        },
        levelFive: {
          phrases: [
            {
              id: 1,
              phrase: '',
              emotion: '',
              acting: '',
              vocal: ''
            }
          ]
        }
      });
      setError(null);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="space-y-6">
            {Array?.from({ length: 8 })?.map((_, i) => (
              <div key={i} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
                <div className="h-6 bg-white bg-opacity-20 rounded-xl shimmer mb-4 w-1/3"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-full"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-3/4"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Song' : 'Add New Song'} - RoleCall</title>
        <meta name="description" content={isEditing ? 'Edit existing song' : 'Add a comprehensive song to the database'} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 relative">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl relative z-0">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate('/song-database-list')}
                className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
                aria-label="Back to song list"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-white">
                  {isEditing ? 'Edit Song' : 'Add New Song'}
                </h1>
                <p className="text-blue-100 font-body mt-1">
                  {isEditing ? 'Update the song details below' : 'Fill out the comprehensive song form'}
                </p>
              </div>
            </div>
            
            {/* Required fields notice */}
            <div className="bg-yellow-500 bg-opacity-20 border border-yellow-400 border-opacity-30 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={20} className="text-yellow-300 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-yellow-200 font-medium mb-1">Required Fields</h3>
                  <p className="text-yellow-100 text-sm">
                    Only <strong>Musical</strong> and <strong>Song Title</strong> are required to submit this form. 
                    All other fields are optional and can be filled in later.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isVisible={showSuccess}
              isOpen={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8 relative">
            {/* Talent Section - NEW above Overview */}
            <TalentSection
              selectedTalentId={selectedTalentId}
              onTalentChange={handleTalentChange}
            />

            {/* Overview Section - Updated with musical dropdown */}
            <OverviewSection
              data={formData?.overview}
              onChange={(data) => handleSectionChange('overview', data)}
              errors={{}}
              songId={songId}
              talentId={selectedTalentId}
              musicals={musicals}
            />

            {/* Song Details Section - Updated to remove unwanted fields */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
                <Icon name="Music" size={24} className="text-blue-200" />
                <span>Song Details</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Act Number */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-2">
                    Act Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData?.songDetails?.actNumber || ''}
                    onChange={(e) => handleSectionChange('songDetails', { 
                      ...formData?.songDetails, 
                      actNumber: parseInt(e?.target?.value) || 1 
                    })}
                    className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="1"
                  />
                </div>

                {/* Song Order */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-2">
                    Song Order
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData?.songDetails?.songOrder || ''}
                    onChange={(e) => handleSectionChange('songDetails', { 
                      ...formData?.songDetails, 
                      songOrder: parseInt(e?.target?.value) || null 
                    })}
                    className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Optional"
                  />
                </div>

                {/* Audio URL */}
                <div>
                  <label className="block text-sm font-medium text-blue-100 mb-2">
                    Audio URL
                  </label>
                  <input
                    type="url"
                    value={formData?.songDetails?.audioUrl || ''}
                    onChange={(e) => handleSectionChange('songDetails', { 
                      ...formData?.songDetails, 
                      audioUrl: e?.target?.value 
                    })}
                    className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="https://example.com/audio.mp3"
                  />
                </div>

                {/* Featured Song Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={formData?.songDetails?.isFeatured || false}
                    onChange={(e) => handleSectionChange('songDetails', { 
                      ...formData?.songDetails, 
                      isFeatured: e?.target?.checked 
                    })}
                    className="w-4 h-4 text-blue-600 bg-white bg-opacity-20 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isFeatured" className="text-sm font-medium text-blue-100">
                    Featured Song
                  </label>
                </div>
              </div>
            </div>

            {/* Level 1 - Single large text box for Lyrics with video upload */}
            <LevelOneSection
              data={formData?.levelOne}
              onChange={(data) => handleSectionChange('levelOne', data)}
              error={null}
              songId={songId}
              talentId={talentId}
            />

            {/* Level 2 - Single large text box for Basic Discussion with video upload */}
            <LevelTwoSection
              data={formData?.levelTwo}
              onChange={(data) => handleSectionChange('levelTwo', data)}
              error={null}
              songId={songId}
              talentId={talentId}
            />

            {/* Level 3 - Audition Prep with 3 subsections and video uploads */}
            <LevelThreeSection
              data={formData?.levelThree}
              onChange={(data) => handleSectionChange('levelThree', data)}
              errors={{}}
              songId={songId}
              talentId={talentId}
            />

            {/* Level 4 - Cast Ready with 6 subsections and video uploads */}
            <LevelFourSection
              data={formData?.levelFour}
              onChange={(data) => handleSectionChange('levelFour', data)}
              errors={{}}
              songId={songId}
              talentId={talentId}
            />

            {/* Level 5 - Mastering with dynamic Phrase subsections and video upload */}
            <LevelFiveSection
              data={formData?.levelFive}
              onChange={(data) => handleSectionChange('levelFive', data)}
              errors={{}}
              songId={songId}
              talentId={talentId}
            />

            {/* Form Actions */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 sticky bottom-6 z-10">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 text-blue-200 hover:text-white font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleClearForm}
                    className="px-4 py-2 text-sm text-blue-200 hover:text-white transition-colors duration-200 border border-white border-opacity-30 rounded-xl hover:bg-white hover:bg-opacity-10"
                  >
                    Clear Form
                  </button>
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`
                    px-8 py-3 bg-white text-indigo-900 font-semibold rounded-xl
                    transition-all duration-200 flex items-center space-x-2
                    ${isLoading 
                      ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 hover:scale-102 shadow-lg'
                    }
                  `}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-900 rounded-full animate-spin"></div>
                      <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <>
                      <Icon name={isEditing ? 'Save' : 'Plus'} size={18} />
                      <span>{isEditing ? 'Update Song' : 'Add Song'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default SongAdditionForm;